// eslint-disable-next-line no-unused-vars
import React from 'react';
import { bool, object, oneOfType, string, arrayOf, func } from 'prop-types';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { ComposedModal, ModalHeader, ModalBody, Loading } from '@carbon/react';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import Carousel from 'react-multi-carousel';
import { useContextSelector } from 'use-context-selector';
import { Package } from 'lucide-react';

import useScreen from 'Hooks/useScreen';
import { UserContext } from 'Contexts/UserContext';
import Price from 'Components/Price/Price';

import Img from 'Containers/Image/Image';
import AddToCart from 'Components/AddToCart/AddToCart';

const StyledComposedModal = styled(ComposedModal)`
  z-index: 9001;
  .cds--modal-container {
    max-width: 820px;
  }
`;

const StyledModalBody = styled(ModalBody)`
  overflow: auto;

  .productDetails {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 0.5rem 1rem;

    .thumb {
      min-width: 130px;
      width: 130px;
      margin-right: 15px;
      text-align: center;

      svg.image {
        max-width: 70%;
      }
      .image {
        max-width: 100%;
        max-height: 100%;
        height: auto;
        width: 100%;
        margin: 0 auto;
      }
    }

    .details {
      display: flex;
      font-size: 0.875rem;
      color: ${ props => props.theme.colors.darkGray };

      p {
        padding-right: 15%;
      }

      .description {
        display: -webkit-box;
        -webkit-line-clamp: 4;
        -webkit-box-orient: vertical;
        overflow: hidden;
        max-height: 80px;
        padding-right: 100px;

        p {
          font-size: inherit;
          color: inherit;
          padding-right: 0;
        }
      }
    }

    .quantity {
      font-weight: 700;
      span {
        margin-left: 2px;
        font-weight: normal;
      }
    }

    .priceContainer {
      height: 60px;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      flex-wrap: wrap;
      min-width: 150px;

      .productInfo .quantity {
        display: none;
      }

      .price {
        margin-right: 15px;
        margin-bottom: 5px;
        font-size: 95%;
        flex-grow: 0;
      }

      a {
        min-width: 0;
        font-size: 0.875rem;
        font-weight: 600;
      }
    }
  }

  .products {
    .ribbon {
      &:after {
        display: block;
        content: '';
        position: absolute;
        bottom: -2px;
        left: 0;
        width: 100%;
        height: 2px;
        max-width: 70px;
        background-color: ${ props => props.theme.colors.default };
      }

      font-size: 1.125rem;
      border-bottom: 2px solid ${ props => props.theme.products.colors.border };
      position: relative;
      font-weight: 600;
      margin-top: 10px;
      margin-right: 15px;
      padding: 10px 1rem 10px 0;
      color: ${ props => props.theme.colors.darkGray };
    }

    > .carousel {
      position: relative;
      left: -15px;
      width: calc(100% + 15px);
      padding-bottom: 20px;

      ul {
        width: 100%;
        justify-content: center;
      }

      li {
        padding: 1rem;
        max-width: 280px;
      }

      .recommendedProduct {
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        border: 1px solid #E9E9E9;

        .preview {
          border-bottom: 1px solid #E9E9E9;
          height: 150px;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;

          a {
            display: block;
            width: 100%;
            max-height: 100%;
            text-align: center;

            .image {
              max-height: 140px;
              max-width: 100%;
              width: auto;
              height: auto;
              margin: 0 auto;
            }
          }
        }

        .details {
          display: flex;
          flex-grow: 1;
          flex-direction: column;
          align-items: center;
          justify-content: space-between;

          > div {
            display: flex;
            flex-direction: column;
            align-items: center;
          }

          .title {
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            padding: 0 10px;
            margin-bottom: 10px;
            color: ${ props => props.theme.colors.darkGray };
            overflow: hidden;
          }

          .price {
            margin-bottom: 10px;
          }

          .addToCartContainer {
            .addToCart {
              padding: 0 15px;
              font-size: 0.875rem;
              min-width: 0;
              max-width: 100%;
              width: 100%;
            }
          }

          padding: 10px 0;
        }
      }
    }

    .react-multi-carousel-dot-list {
      .react-multi-carousel-dot--active {
        button {
          background-color: #FF8519;
        }
      }

      button {
        border: 0;
        background-color: #A8A8A8;
      }
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    .products {
      > .carousel {
        ul {
          width: auto;
        }
      }
    }

    .productDetails {
      padding: 0;
      flex-direction: column;
      gap: 15px;

      .details {
        .description {
          padding-right: 0;
        }
      }

      .priceContainer {
        height: auto;

        a {
          min-width: 100%;
          justify-content: center;
          min-height: 40px;
        }

        .productInfo {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;

          .quantity {
            display: inline-block;
          }
        }

        .price {
          margin-right: 0;
        }
      }

      .main .quantity {
        display: none;
      }

      .thumb {
        width: 75px;
        min-width: 75px;
      }
    }
  }
`;

const CrossSalesPopup = (
  { open, product, addToCartLoading, products, onAddToCart, onClose }
) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const userData = useContextSelector(UserContext, ({ data }) => data);
  const withVat = userData?.withVat;
  const { width } = useScreen();

  if (!product) return null;
  return (
    <StyledComposedModal
      open={ open }
      onClose={ onClose }
      className="CrossSalesModal"
      size="md"
      selectorPrimaryFocus=".cds--modal-content"
    >
      <ModalHeader>
        <h4>{ t('products.addedToCart') }</h4>
      </ModalHeader>
      <StyledModalBody theme={ theme }>
        <div className="productDetails">
          <div className="details">
            <div className="thumb">
              <Img
                className="image"
                src={ [
                  product?.picture,
                  product?.pictures?.at(0)?.urlThumbnailPicture
                ] }
                loader={ <Loading active small withOverlay={ false } /> }
                unloader={ <Package size={ 50 } strokeWidth={ 1.25 } /> }
                alt={ product.name || product.title }
              />
            </div>
            <div className="main">
              <div className="description"><p>{ product.description || product.title || product.name }</p></div>
              <p className="quantity">
                { t('products.quantity') }:
                <span>{ product.quantity }</span>
              </p>
            </div>
          </div>
          <div className="priceContainer">
            <div className="productInfo">
              <p className="quantity">
                { t('products.quantity') }:
                <span>{ product.quantity }</span>
              </p>
              <Price
                price={ product.price * (product.quantity || 1) }
                promoPrice={ product.promoPrice * (product.quantity || 1) }
                withTooltip={ false }
              />
            </div>
            <Link
              role="button"
              className="cds--btn cds--btn--ghost red"
              to="/checkout"
            >
              { t('cart.viewDetails') }
            </Link>
          </div>
        </div>
        <div className="products">
          <p className="ribbon">
            { t('products.sectionTitles.recommendedProducts') }
          </p>
          <Carousel
            className="carousel"
            ssr={ false }
            deviceType={ width < 768 ? 'mobile' : 'desktop' }
            arrows={ false }
            autoPlay={ false }
            showDots={ false }
            renderDotsOutside={ false }
            shouldResetAutoplay={ false }
            responsive={ {
              desktop: {
                breakpoint: { max: 6000, min: 1540 },
                items: 3
              },
              tablet: {
                breakpoint: { max: 1539, min: 1072 },
                items: 3
              },
              mobile: {
                breakpoint: { max: 767, min: 0 },
                items: 1.75,
              }
            } }
          >
            { products.map(prod => (
              <div className="recommendedProduct" key={ prod.id }>
                <div className="preview">
                  <Link
                    to={ `/products/${ prod.slug }/pd/${ prod.id }` }
                    state={ {
                      component: 'cross_sales_popup',
                      source: 'recommended_products',
                      clickedOn: 'image',
                      productId: prod.id
                    } }
                  >
                    <Img
                      className="image"
                      src={ prod.pictures?.at(0)?.urlThumbnailPicture }
                      unloader={ <Package size={ 50 } strokeWidth={ 1.25 } /> }
                      alt={ prod.title }
                      loader={ <Loading active small withOverlay={ false } /> }
                    />
                  </Link>
                </div>
                <div className="details">
                  <Link
                    className="title"
                    to={ `/products/${ prod.slug }/pd/${ prod.id }` }
                    state={ {
                      component: 'cross_sales_popup',
                      source: 'recommended_products',
                      clickedOn: 'title',
                      productId: prod.id
                    } }
                  >
                    { prod.title }
                  </Link>
                  <div>
                    { !prod?.hasPriceRequest && !prod?.warnings?.blocked_prices && (
                      <Price
                        price={ prod.price }
                        promoPrice={ prod.promoPrice }
                        withTooltip={ false }
                        withVat={ withVat }
                        displayVatInfo
                      />
                    ) }
                    <AddToCart
                      showSelector={ false }
                      step={ Math.max(prod.minQuantity, 1) }
                      min={ Math.max(prod.minQuantity, 1) }
                      max={ prod.stockValue }
                      onClick={ () => onAddToCart(prod) }
                      trackingContext={ {
                        location: 'cross_sales_popup',
                        product_sku: prod?.code,
                      } }
                      disabled={
                        prod.minQuantity > prod.stockValue
                        || prod.warnings?.blocked_prices
                        || prod.warnings?.blocked_stocks
                        || prod.warnings?.blocked_orders
                        || prod?.hasPriceRequest
                      }
                      loading={ addToCartLoading === prod.id }
                      size="md"
                    />
                  </div>
                </div>
              </div>
            )) }
          </Carousel>
        </div>
      </StyledModalBody>
    </StyledComposedModal>
  );
};

CrossSalesPopup.propTypes = {
  open: bool,
  product: object,
  addToCartLoading: oneOfType([string, bool]),
  products: arrayOf(object),
  onClose: func,
  onAddToCart: func
};

export default CrossSalesPopup;
